import React from 'react';
import { Alert, Col, Form, Row } from 'reactstrap';

import C from '../C';

import DataStore, { getUrlValue } from '../base/plumbing/DataStore';
import Misc from '../base/components/Misc';
import { Card } from '../base/components/CardAccordion';
import PropControl from '../base/components/PropControl';
import { getDataLogData, pivotDataLogToRows } from '../base/plumbing/DataLog';
import { encURI, is, uniq, yessy } from '../base/utils/miscutils';
import { getDataItem, getDataList } from '../base/plumbing/Crud';
import KStatus from '../base/data/KStatus';
import SimpleTable from '../base/components/SimpleTable';
import LinkOut from '../base/components/LinkOut';
import Logo from '../base/components/Logo';
import NewChartWidget from '../base/components/NewChartWidget';
import ErrAlert from '../base/components/ErrAlert';
import { modifyPage } from '../base/plumbing/glrouter';
import SearchQuery from '../base/searchquery';
import PropControlPeriod from '../base/components/propcontrols/PropControlPeriod';
import List from '../base/data/List';
import SubCard from '../base/components/SubCard';
import Icon from '../base/components/Icon';
import MDText from '../base/components/MDText';
import { MONTHS, dateStr } from '../base/utils/date-utils';

import { gdi, itemAccessor } from './greentag/greenTagPageUtils';


function CarbonPage() {
	return (<>
		<h1>Carbon Footprint Page</h1>
		<div>
			<LinkOut href="https://good-loop.monday.com/boards/3136989183">Green Deals Board</LinkOut> |
			<LinkOut href="https://good-loop.monday.com/boards/2613874238">Donations Board</LinkOut> |
			<C.A href="/#impactallocation">Impact Allocation</C.A>
		</div>
		<SubCard title="Instructions to the Alpha-Version Bleeding Edge Billing Bot 🤖">
			<p><Icon name="bug" /> May contain bugs! Please report any you find with the url and a screenshot.</p>			
			<MDText source={`
Some steps are still manual:

 - *For now: You need to apply this separately for each Agency -- and only the GroupM AU+NZ ones.*
   - Group M are not an agency here -- it's the pieces of Group M like Mindshare Australia who we bill.
   - TODO bill BrandAdvance (quarterly) and Captify
 - Optional positive actions like coral and tree planting -- These _might_ be handled. Please do a manual check.
   - *Add any coral and trees to Xero manually:*
     - As a line-item in the agency invoice
	 - And as a bill from the charity
	 - And also to Monday Donations
     - Use the Bill ID to make a DOnation ID (which is Bill ID + charity) and use this for tracking.
 - In Xero, the company and the prices might be marked as TODO. This must be checked before sending out.
 - In Monday, the Donation row might not have much data -- including the amount. You can leave this as we'll automate it later from Portal.
 - In Portal, the ImpactDebits might not have £s. You can leave this as we'll automate it later from Xero.

`}
			/>
		</SubCard>
		<BreakdownByAgency />

		<div className="mt-1">
			<LinkOut href="https://github.com/good-loop/icon/tree/master/watermark">Green Watermark / Badge images</LinkOut>
		</div>
	</>);
}

const DAY_MSECS = 1000 * 60 * 60 * 24;


/**
 * 
 * @param {Object} p
 * @returns {?ImpactDebit[]} null whilst loading
 */
const impactsForRow = ({row, end}) => {
	if ( ( ! row.campaign && ! row.agency) || ! end) return null;
	// lookup by campaign or by agency?
	let iq = SearchQuery.setProp(null, "campaign", row.campaign).query;
	if ( ! row.campaign) {
		iq = SearchQuery.setProp("campaign:unset", "agencyId", row.agency).query;
	}
	// query end robustly - allow +/-1 day
	// ??do we have utility functions for date arithmetic?
	let e1 = new Date(end).getTime() - DAY_MSECS;
	let e1d = new Date(e1);
	let e2 = new Date(end).getTime() + DAY_MSECS;
	let e2d = new Date(e2);
	iq += " end:before:" + e2d.getTime() + " and end:after:" + e1d.getTime();
	let pvList = getDataList({ type: "ImpactDebit", status: KStatus.PUB_OR_DRAFT, q: iq });
	if (!pvList.value) return null;
	let hits = List.hits(pvList.value);
	if (!yessy(hits)) return null;
	return hits;
};

/**
 * 
 * @param {!string} dateString 
 * @returns true if e.g. "2023-02-01"
 */
const isShortDate = (dateString) => {
	const regex = /^\d{4}-\d{2}-\d{2}$/;
	return regex.test(dateString);
};

function BreakdownByAgency() {
	let sq = null;
	// Filter by brand/agency
	const vertiser = DataStore.getUrlValue('brand');
	const agencyId = DataStore.getUrlValue('agency');
	const agency = agencyId && getDataItem({type:"Agency",id:agencyId, status:KStatus.PUBLISHED}).value;
	if (vertiser) {
		sq = SearchQuery.setProp(sq, 'vertiser', vertiser);
	}
	if (agencyId) {
		sq = SearchQuery.setProp(sq, 'agency', agencyId);
	}
	const q = sq && sq.query;

	let start = DataStore.getUrlValue("start");
	let end = DataStore.getUrlValue("end");
	// HACK: Mar 2023 workaround for fencepost issue https://good-loop.monday.com/boards/2603585504/views/60487313/pulses/4070182568
	if (end && end.substring(end.length-3, end.length)==="-01") {
		end = (new Date(end).getTime() - 1); // TODO delete this once DataServlet has include flags
	}

	if (isShortDate(end)) {
		end = end + 'T23:59:59Z'; // go to the end of the day
	}
	// sampling prob - default to -1 at first but always use 1 (all data) for billing
	let prob = getUrlValue("prob");
	if (agencyId || vertiser) {
		prob = DataStore.setUrlValue("prob", 1);
	} else if ( ! is(prob)) {
		prob = DataStore.setUrlValue("prob", -1);
	}
	let breakdowns = [DataStore.getUrlValue("breakdown")]; // NB: agency is now logged as `agency` -- but historically, no logging except tag=adid and vertiser :(
	let pvData = getDataLogData({ dataspace: "emissions", q, start, end, prob, breakdowns });
	let emissionsData = pvData?.value?.sampling || pvData?.value; // handle optional sampling
	let dataRows = emissionsData && breakdowns && breakdowns[0] && pivotDataLogToRows(emissionsData, breakdowns[0]);
	// let campaignIds = dataRows && dataRows.map(row => row.campaign);
	// console.log("dataRows", dataRows);

	// TODO fetch the Impacts for each campaign

	let row2impId = row => {
		let imps = impactsForRow({row,end});
		if ( ! yessy(imps)) return 0;
		return imps[0].id;
	};

	/**
	 * @param {Object} row 
	 * @returns {?string} external campaign ref if there is one (null while loading)
	 */
	const xrefFromRow = row => {
		if ( ! row.campaign) return null;
		const pvObj = gdi("Campaign", row.campaign);
		if ( ! pvObj.value) return null;
		if (pvObj.value.xref==="null") return null; //?? Mar 2023 oddity
		return pvObj.value.xref;
	};

	/** @type{Column[]} */
	let columns = //dataRows? Object.keys(dataRows[0]) : ["count"];
		[{ Header: "Agency", accessor: item => itemAccessor("Agency",item.agency), 
			Cell: (name,col,item) => <ABC id={item.agency} type={C.TYPES.Agency} />, total: false },
		{ Header: "Brand", accessor: item => itemAccessor("Advertiser",item.vertiser), 
			Cell: (name,col,item) => <ABC id={item.vertiser} type={C.TYPES.Advertiser} />, total: false },
		{ Header: "Campaign", accessor: row => itemAccessor("Campaign",row.campaign), 
			Cell: (name,col,row) => <ABC id={row.campaign} type={C.TYPES.Campaign} />, total: false },
		{ Header: "Campaign ID", accessor: row => row.campaign, total: false },
		{ Header: "Campaign External Ref", accessor: xrefFromRow, total: false },
		{ Header: "ImpactDebits", accessor: row2impId, Cell: iid => <ABC id={iid} type={C.TYPES.ImpactDebit} />, total: false },
		{ Header: "Impressions", accessor: 'count' },
		{ Header: "CO2", accessor: 'co2' },
		// { Header: "CO2 creative", accessor: 'co2creative' }, // dont care here
		// { Header: "CO2 base", accessor: 'co2base' },
		// { Header: "CO2 supply path", accessor: 'co2supplypath' },
		// { Header: "TODO positive-impact" },
		];
	// add a column for Month? (requested by Tom Oct 2023)
	let month = start && end && (new Date(start).getTime() - new Date(end).getTime()) < 1000*60*60*24*32 && 
				MONTHS[new Date(start).getMonth()];
	if (month) {
		columns.push({ Header: "Month", accessor: () => month});
	}
	let pvTimeData = getDataLogData({
		name: "TimeData", dataspace: "emissions", q, start, end, prob: 0.1, breakdowns: ["time"]
	});
	let timeLabels = [];
	let timeData = [];
	let timeDataValue = pvTimeData?.value?.sampling || pvTimeData?.value; // handle optional sampling
	if (timeDataValue) {
		let buckets = timeDataValue.by_time.buckets;
		// copy pasta labelFn from my-loop TimeSeriesCardEmissions
		// // Omit year in labels if the period doesn't span a year boundary
		// const labelFn = (period.start.getYear() === period.end.getYear()) ? (
		// 	utc => printDateShort(new Date(utc))
		// ) : (
		// 	utc => printDate(new Date(utc))
		// );
		buckets.forEach((b, i) => {
			let d = dateStr(new Date(b.key));
			timeLabels.push(d);
			timeData.push(b.count);
		});
	}
	let data = { labels: timeLabels, datasets: [{ data: timeData }] };
	const options = {};

	return <Card title="Impressions by Agency">
		<PropControlPeriod label="Report Period" buttons={["last-quarter","last-month","this-month","yesterday"]} />
		<Form className="border pl-2 pr-2 -pt-1">
			<label>Filter</label>
			<Row>
				<Col>
					<PropControl prop="agency" label type="DataItem" itemType="Agency" />
				</Col><Col>
					<PropControl prop="brand" label type="DataItem" itemType={C.TYPES.Advertiser} />
				</Col>
			</Row>
		</Form>
		<PropControl prop="breakdown" label type="select"
			options={['agency/vertiser/campaign{"emissions":"sum"}', 'adid{"emissions":"sum"}', 'campaign{"emissions":"sum"}', 'vertiser{"emissions":"sum"}', 'agency{"emissions":"sum"}', 'vertiser/campaign{"emissions":"sum"}']}
			dflt='agency/vertiser/campaign{"emissions":"sum"}'
		/>
		<PropControl type="number" label="Sampling Probability" prop="prob" help="Use -1 for auto-calculate; 1 to turn off sampling. Hard set to 1 for billing." 
			disabled={agencyId || vertiser} />
		<h4>Activity</h4>
		<NewChartWidget data={data} options={options} height={null} width={null} legend={false} />
		<BillingButtons {...{ agencyId, dataRows, vertiser, start, end }} />
		{yessy(dataRows) && <SimpleTable data={dataRows} columns={columns} hasCsv="top" addTotalRow 
			tableName={[agency && agency.name,"carbon",start && (""+start).substring(0,7)].filter(x => x).join("-")} 
		/>}
		{!pvData.resolved && <Misc.Loading />}
		<ErrAlert error={pvData.error} />
	</Card>;
}

function BillingButtons({ dataRows, agencyId, vertiser, start, end }) {
	if (!agencyId || !end || !start) {
		let allAgencyIds = dataRows? uniq(dataRows.map(row => row.agency)) : [];
		return <div>Set Agency and start/end date to do billing. Here are the ones in this data-set:
			{allAgencyIds.map(aid => <ABC key={aid} id={aid} type="Agency" />)}
			</div>;
	}
	let agency = agencyId && getDataItem({type:"Agency", id:agencyId, status:KStatus.PUBLISHED}).value;
	let path = ['widget', 'billing', agencyId];
	let billing = DataStore.getValue(path) || DataStore.setValue(path, {});
	let endDate = new Date(end).toISOString().substring(0, 10);
	Object.assign(billing, {agencyId, vertiser, start, end}); // info for the server

	let row = {agency:agencyId};
	let hits = impactsForRow({row, end}) || [];

	const setBillId = jobNumber => {
		// quarter or month?
		let qm = new Date(end).getTime() - new Date(start).getTime() > (40*24*60*60*1000)? "Q" : "M";
		// one day back because "ends at 00:00 on 1st March" is really "end of Feb"
		let endDateMinus1 = new Date(new Date(end).getTime()-1000).toISOString().substring(0, 10);
		let billId = jobNumber +"_"+qm+"_" + endDateMinus1;
		DataStore.setValue(path.concat("billId"), billId);
	};
	if (billing.jobNumber && ! billing.billId) {
		setBillId(billing.jobNumber);
	}

	return <SubCard title={<span>For Agency <ABC type="Agency" id={agencyId} linkToPage />, time period: {start} to {end}</span>}>
		{agency && <>
			{ ! agency.xero && <Alert color="warning">No Xero ID set for this agency</Alert>}
			{ ! agency.paymentSettings?.currency && <Alert color="warning">No currency set for this agency</Alert>}
			{ ! agency.paymentSettings?.measurementCPM && <Alert color="warning">No Measurement CPM set for this agency</Alert>}			
		</>}

		See <LinkOut href="https://docs.google.com/spreadsheets/d/1n7CKYJWHJxq-u8q8J03GtnK_xZtvttbnHlh0SmHTyQg/edit#gid=977928946">Design Doc: Green Media finance process - From lead to offset, Xaxis to Volvo planting coral</LinkOut>
		<Row>
			<Col md={3}>
				<PropControl prop="jobNumber" path={path}
					label="Job Number"
					dflt={"green_"+agencyId}
					saveFn={({ value }) => setBillId(value)}
				/>
			</Col><Col md={3} >
				<PropControl prop="billId" path={path} label="Bill ID" />
			</Col><Col md={3} >
				Agency ImpactDebits
				{hits.map(hit => <ABC key={hit.id} type="ImpactDebit" id={hit.id} />)}
				{/* <PropControlDataItemList label="TODO Select Agency-level ImpactDebit" path={path} prop="agencyLevel"
					itemType="ImpactDebit"
					linkProp="agencyId" linkValue={agencyId}
				/> */}
			</Col><Col md={3} >
				<Misc.SubmitButton 
					disabled={ !! [agencyId, start, end, billing.jobNumber, billing.billId].filter(x => ! x).length}
					path={path}
					url={`${ServerIO.APIBASE}/billing?action=BILL_AGENCY_OFFSETS`}
					color='primary' confirmSubmit={"Are you sure you want to create bill "+billing.billId+"?"}
				>
					Create ImpactDebits and <i>draft</i> Xero invoice + bills, and a Monday Donation row
				</Misc.SubmitButton>
			</Col>
		</Row>
	</SubCard>;
}

/**
 * Logo+Text+link for a DataItem. Link is to (Agency|Advertiser) add a url parameter / (other) that item's page
 * @param {Object} p
 * @param {String} p.type
 * @param {String} p.id
 * @param {?boolean} p.linkToPage HACK set true to make this link to the page (not add a url parameter)
 */
export function ABC({ type, id, linkToPage }) {
	if (!id || id === "unset") {
		return <span />;
	}
	let pvItem = getDataItem({ type, id, status: KStatus.PUB_OR_DRAFT, swallow: true });
	let name = pvItem?.value?.name || id;
	let href = '/#' + type.toLowerCase() + '/' + encURI(id);
	if (type === C.TYPES.Agency && ! linkToPage) {
		href = modifyPage(null, { agency: id }, true);
	} if (type === C.TYPES.Advertiser && ! linkToPage) {
		href = modifyPage(null, { brand: id }, true);
	}
	return <C.A href={href}><Logo item={pvItem.value} size="sm" logoOnly /> {name}</C.A>;
}

export default CarbonPage;
